/* FOCAS theme behaviours — scroll reveal, story carousel, certificate stack,
   FAQ accordion and the shared video lightbox. Written to survive Shopify
   theme-editor section re-renders (see the shopify:section:load listener). */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- reveal */

  function initReveal(root) {
    var targets = root.querySelectorAll('.reveal:not([data-reveal-bound])');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-visible');
        el.setAttribute('data-reveal-bound', 'true');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );

    targets.forEach(function (el) {
      el.setAttribute('data-reveal-bound', 'true');
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------- video lightbox */

  function ensureModal() {
    var modal = document.getElementById('FocasVideoModal');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.id = 'FocasVideoModal';
    modal.className = 'video-modal';
    modal.setAttribute('data-open', 'false');
    modal.innerHTML =
      '<div class="video-modal__body">' +
      '<button type="button" class="video-modal__close" data-video-close aria-label="Close video">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" width="20" height="20"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
      '</button>' +
      '<div class="video-modal__frame"></div>' +
      '</div>';
    document.body.appendChild(modal);

    modal.addEventListener('click', function (event) {
      if (event.target === modal || event.target.closest('[data-video-close]')) closeModal();
    });

    return modal;
  }

  function closeModal() {
    var modal = document.getElementById('FocasVideoModal');
    if (!modal) return;
    modal.setAttribute('data-open', 'false');
    modal.querySelector('.video-modal__frame').innerHTML = '';
    document.body.style.overflow = '';
  }

  function youtubeId(url) {
    if (!url) return '';
    var match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
    return match ? match[1] : '';
  }

  function openModal(url, orientation) {
    if (!url) return;

    var modal = ensureModal();
    var body = modal.querySelector('.video-modal__body');
    var frame = modal.querySelector('.video-modal__frame');

    body.classList.toggle('video-modal__body--portrait', orientation === 'portrait');

    var ytId = youtubeId(url);
    if (ytId) {
      frame.innerHTML =
        '<iframe src="https://www.youtube.com/embed/' +
        ytId +
        '?autoplay=1&rel=0" title="Video" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>';
    } else {
      frame.innerHTML =
        '<video src="' + url + '" controls autoplay playsinline preload="metadata"></video>';
    }

    modal.setAttribute('data-open', 'true');
    document.body.style.overflow = 'hidden';
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-video-url]');
    if (!trigger) return;
    var url = trigger.getAttribute('data-video-url');
    if (!url) return;
    event.preventDefault();
    openModal(url, trigger.getAttribute('data-video-orientation') || 'landscape');
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeModal();
  });

  /* ------------------------------------------------------ story carousel */

  function initCarousels(root) {
    root.querySelectorAll('[data-carousel]:not([data-carousel-bound])').forEach(function (carousel) {
      carousel.setAttribute('data-carousel-bound', 'true');

      var track = carousel.querySelector('[data-carousel-track]');
      var prev = carousel.querySelector('[data-carousel-prev]');
      var next = carousel.querySelector('[data-carousel-next]');
      if (!track) return;

      function step() {
        var slide = track.querySelector('.stories__slide');
        return slide ? slide.getBoundingClientRect().width : track.clientWidth * 0.8;
      }

      if (prev) {
        prev.addEventListener('click', function () {
          track.scrollBy({ left: -step(), behavior: 'smooth' });
        });
      }

      if (next) {
        next.addEventListener('click', function () {
          track.scrollBy({ left: step(), behavior: 'smooth' });
        });
      }
    });
  }

  /* --------------------------------------------------- certificate stack */

  function initCertStacks(root) {
    root.querySelectorAll('[data-cert-stack]:not([data-cert-bound])').forEach(function (stack) {
      stack.setAttribute('data-cert-bound', 'true');

      var items = Array.prototype.slice.call(stack.querySelectorAll('.cert-stack__item'));
      if (items.length < 2) return;

      var offset = 0;

      function paint() {
        items.forEach(function (item, index) {
          var pos = (index - offset + items.length) % items.length;
          item.setAttribute('data-pos', pos > 2 ? 'hidden' : String(pos));
        });
      }

      function advance() {
        offset = (offset + 1) % items.length;
        paint();
      }

      paint();

      stack.addEventListener('click', advance);
      stack.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          advance();
        }
      });

      var startX = null;
      stack.addEventListener(
        'touchstart',
        function (event) {
          startX = event.touches[0].clientX;
        },
        { passive: true }
      );
      stack.addEventListener('touchend', function (event) {
        if (startX === null) return;
        if (Math.abs(event.changedTouches[0].clientX - startX) > 40) advance();
        startX = null;
      });
    });
  }

  /* ------------------------------------------------------- FAQ accordion */

  function initAccordions(root) {
    root.querySelectorAll('[data-accordion]:not([data-accordion-bound])').forEach(function (accordion) {
      accordion.setAttribute('data-accordion-bound', 'true');

      var single = accordion.getAttribute('data-accordion') !== 'multiple';

      accordion.querySelectorAll('[data-accordion-trigger]').forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var panel = document.getElementById(trigger.getAttribute('aria-controls'));
          var isOpen = trigger.getAttribute('aria-expanded') === 'true';

          if (single) {
            accordion.querySelectorAll('[data-accordion-trigger]').forEach(function (other) {
              if (other === trigger) return;
              other.setAttribute('aria-expanded', 'false');
              var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
              if (otherPanel) otherPanel.setAttribute('data-open', 'false');
            });
          }

          trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          if (panel) panel.setAttribute('data-open', isOpen ? 'false' : 'true');
        });
      });
    });
  }

  /* --------------------------------------------------- registration modal */

  var lastFocused = null;

  function registerModal() {
    return document.querySelector('[data-counsel-modal]');
  }

  function openRegister() {
    var modal = registerModal();
    if (!modal) return false;

    lastFocused = document.activeElement;
    modal.setAttribute('data-open', 'true');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    var first = modal.querySelector('.counsel-input input, input.counsel-input');
    if (!first) first = modal.querySelector('[data-counsel-close]');
    if (first) first.focus();

    return true;
  }

  function closeRegister() {
    var modal = registerModal();
    if (!modal || modal.getAttribute('data-open') !== 'true') return;

    modal.setAttribute('data-open', 'false');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (lastFocused && lastFocused.focus) lastFocused.focus();
    lastFocused = null;
  }

  /* Anchors keep their href so the page still works without JS — only take
     over the click once we know the modal is actually on the page. */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-counsel-form-open]');
    if (!trigger || !registerModal()) return;
    event.preventDefault();
    openRegister();
  });

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-counsel-close]')) closeRegister();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeRegister();
  });

  function fieldOf(input) {
    return input ? input.closest('.counsel-field') : null;
  }

  function setValid(input, valid) {
    var field = fieldOf(input);
    if (!field) return;

    field.setAttribute('data-invalid', valid ? 'false' : 'true');
    var error = field.querySelector('[data-counsel-error]');
    if (error) error.hidden = valid;
  }

  /* State/city options live in counsel-locations.js, which is deferred — poll
     briefly rather than assume it has parsed by the time we bind. */
  function initLocations(form) {
    var state = form.querySelector('[data-counsel-state]');
    var city = form.querySelector('[data-counsel-city]');
    if (!state || !city) return;

    var attempts = 0;

    function fill() {
      var data = window.COUNSEL_LOCATIONS;
      if (!data) {
        if (attempts++ < 40) return window.setTimeout(fill, 100);
        return;
      }

      Object.keys(data).forEach(function (name) {
        var option = document.createElement('option');
        option.value = name;
        option.textContent = name;
        state.appendChild(option);
      });

      state.addEventListener('change', function () {
        var cities = data[state.value] || [];

        city.innerHTML = '';
        var head = document.createElement('option');
        head.value = '';
        head.disabled = true;
        head.selected = true;
        head.textContent = cities.length
          ? city.getAttribute('data-city-placeholder')
          : city.getAttribute('data-city-empty');
        city.appendChild(head);

        cities.forEach(function (name) {
          var option = document.createElement('option');
          option.value = name;
          option.textContent = name;
          city.appendChild(option);
        });

        city.disabled = !cities.length;
        setValid(state, true);
      });
    }

    fill();
  }

  /* ------------------------------------------------- Zoho Flow -> Bigin */

  /* The lead is pushed to a Zoho Flow webhook, which creates the Bigin
     contact + pipeline record. Two constraints shape this:

       1. The form submits natively straight after (to /cart/add or /contact),
          so the page unloads. sendBeacon is the only send that survives that;
          fetch({keepalive}) is the fallback for browsers without it.
       2. Zoho Flow serves no CORS headers, so the response is unreadable and
          any non-simple request would be blocked at preflight. Form-encoding
          keeps it a simple request and Flow reads it as "Form data".

     Delivery is therefore fire-and-forget — treat the Shopify order or
     contact-form entry as the record of truth, not this call. */
  function flowValue(form, selector) {
    var el = form.querySelector(selector);
    return el && el.value ? el.value.trim() : '';
  }

  function buildFlowPayload(form) {
    var dial = flowValue(form, '[data-counsel-dial]');
    var phone = flowValue(form, '[data-counsel-phone]');
    var state = flowValue(form, '[data-counsel-state]');
    var city = flowValue(form, '[data-counsel-city]');

    var payload = {
      first_name: flowValue(form, '#CounselFirstName'),
      last_name: flowValue(form, '#CounselLastName'),
      dial_code: dial,
      phone: phone,
      /* Bigin's Phone field is a single string — send it pre-joined so Flow
         does not have to concatenate. */
      full_phone: dial ? dial + phone : phone,
      ca_status: flowValue(form, '#CounselStatus'),
      attempt: flowValue(form, '#CounselAttempt'),
      state: state,
      city: city,
      location: state && city ? city + ', ' + state : state || city,
      language: flowValue(form, '#CounselLanguage'),
      lead_source: form.getAttribute('data-counsel-lead-source') || 'Organic',
      page_url: window.location.href,
      submitted_at: new Date().toISOString()
    };

    var body = new URLSearchParams();
    Object.keys(payload).forEach(function (key) {
      body.append(key, payload[key]);
    });

    return body;
  }

  function sendToFlow(form) {
    var url = form.getAttribute('data-counsel-webhook');
    if (!url) return;

    /* Editor previews would otherwise create junk leads in Bigin. */
    if (window.Shopify && window.Shopify.designMode) return;

    /* A failed native submit can re-fire this handler; one lead is enough. */
    if (form.getAttribute('data-counsel-sent') === 'true') return;
    form.setAttribute('data-counsel-sent', 'true');

    var body = buildFlowPayload(form);

    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return;

      fetch(url, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      }).catch(function () {});
    } catch (error) {
      /* Never let a CRM hiccup block the shopper from checking out. */
    }
  }

  /* Zoho Flow answers with an opaque response — the browser will not let us
     read whether Bigin actually took the lead — so this is optimistic by
     necessity. The panel is swapped rather than the modal closed, otherwise a
     shopper who saw nothing happen would submit again. */
  function showSuccess(form) {
    var panel = form.parentNode.querySelector('[data-counsel-success]');
    if (!panel) return;

    form.hidden = true;
    panel.hidden = false;

    var title = panel.querySelector('.counsel-success__title');
    if (title) {
      title.setAttribute('tabindex', '-1');
      title.focus();
    }
  }

  /* ------------------------------------------------- checkout hand-off */

  /* /cart/add appends a line every time, and two submissions never collapse
     into one because their line item properties differ — so a shopper who
     fills the form twice arrives at checkout owing 2x the deposit. Drop any
     deposit already sitting in the cart before adding the new one.

     Only lines matching this variant are touched: someone who came to the
     counselling page mid-purchase keeps the course in their cart. */
  function dropExistingDeposit(variantId) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        return cart.items
          .filter(function (item) {
            return String(item.variant_id) === String(variantId);
          })
          .reduce(function (chain, item) {
            /* Sequential — concurrent writes to the cart are rejected. */
            return chain.then(function () {
              return fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: item.key, quantity: 0 })
              });
            });
          }, Promise.resolve());
      });
  }

  /* Mirrors what the native post would have sent: the variant plus every
     answer as a line item property, so the deposit order still carries the
     student's details. Disabled fields are skipped, matching the browser. */
  function checkoutBody(form) {
    var body = new URLSearchParams();
    body.append('id', form.querySelector('input[name="id"]').value);
    body.append('quantity', '1');

    form.querySelectorAll('[name^="properties["]').forEach(function (field) {
      if (!field.disabled) body.append(field.name, field.value);
    });

    return body;
  }

  function goToCheckout(form) {
    var variantId = form.querySelector('input[name="id"]').value;

    dropExistingDeposit(variantId)
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: checkoutBody(form).toString()
        });
      })
      .then(function () {
        window.location.href = '/checkout';
      })
      .catch(function () {
        /* Payment must never be swallowed. Fall back to the native post —
           a duplicate line beats a dead button. */
        form.submit();
      });
  }

  function initRegisterForm(root) {
    root.querySelectorAll('[data-counsel-form]:not([data-counsel-bound])').forEach(function (form) {
      form.setAttribute('data-counsel-bound', 'true');

      var consent = form.querySelector('[data-counsel-consent]');
      var submit = form.querySelector('[data-counsel-submit]');
      var hint = form.querySelector('[data-counsel-hint]');
      var phone = form.querySelector('[data-counsel-phone]');

      /* The deposit is non-trivial, so payment stays locked until the terms
         checkbox is ticked — mirrors the disabled state in the design. */
      if (consent && submit) {
        var syncConsent = function () {
          submit.disabled = !consent.checked;
          if (hint) hint.hidden = consent.checked;
        };
        consent.addEventListener('change', syncConsent);
        syncConsent();
      }

      var dial = form.querySelector('[data-counsel-dial]');

      /* +91 numbers are always 10 digits; other countries vary, so only the
         Indian case gets the strict rule. */
      var phoneOk = function () {
        var digits = phone ? phone.value.trim() : '';
        if (dial && dial.value !== '+91') return /^[0-9]{6,15}$/.test(digits);
        return /^[0-9]{10}$/.test(digits);
      };

      if (phone) {
        phone.addEventListener('input', function () {
          var digits = phone.value.replace(/\D/g, '').slice(0, 15);
          if (digits !== phone.value) phone.value = digits;
          if (phoneOk()) setValid(phone, true);
        });
      }

      initLocations(form);

      form.addEventListener('submit', function (event) {
        var ok = true;

        function require(selector, test) {
          var el = form.querySelector(selector);
          if (!el) return;
          var valid = test(el);
          setValid(el, valid);
          ok = ok && valid;
        }

        var notBlank = function (el) {
          return el.value.trim().length > 0;
        };

        require('#CounselFirstName', function (el) {
          return el.value.trim().length > 1;
        });
        require('#CounselLastName', notBlank);

        if (phone) {
          var valid = phoneOk();
          setValid(phone, valid);
          ok = ok && valid;
        }

        require('#CounselStatus', notBlank);
        require('#CounselAttempt', notBlank);
        require('#CounselLanguage', notBlank);

        var state = form.querySelector('[data-counsel-state]');
        var city = form.querySelector('[data-counsel-city]');
        if (state && city) {
          var locationOk = state.value !== '' && (city.disabled || city.value !== '');
          setValid(state, locationOk);
          ok = ok && locationOk;
        }

        if (!ok) {
          event.preventDefault();
          var firstBad = form.querySelector('[data-invalid="true"] input');
          if (firstBad) firstBad.focus();
          return;
        }

        sendToFlow(form);

        if (submit) {
          submit.disabled = true;
          submit.setAttribute('aria-busy', 'true');
        }

        /* Contact mode has no Shopify destination worth reaching — Bigin is
           the only home for these leads — and Shopify's /contact endpoint
           demands an email the form no longer collects. So stop the native
           post and confirm in place. */
        if (form.getAttribute('data-counsel-mode') !== 'checkout') {
          event.preventDefault();
          showSuccess(form);
          return;
        }

        /* Checkout mode goes through the cart API instead of posting, so a
           re-submit replaces the earlier deposit rather than stacking on top
           of it. Browsers without fetch keep the native post. */
        if (window.fetch && window.Promise && window.URLSearchParams) {
          event.preventDefault();
          goToCheckout(form);
        }
      });
    });
  }

  /* ------------------------------------------- header height measurement */

  function measureHeader() {
    var header = document.querySelector('.site-header');
    if (header) {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    }

    var cta = document.querySelector('.sticky-cta');
    document.documentElement.style.setProperty(
      '--sticky-cta-height',
      (cta ? cta.offsetHeight + 24 : 0) + 'px'
    );
  }

  /* ------------------------------------------------------------ bootstrap */

  function init(root) {
    var scope = root || document;
    initReveal(scope);
    initCarousels(scope);
    initCertStacks(scope);
    initAccordions(scope);
    initRegisterForm(scope);
    measureHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  window.addEventListener('resize', measureHeader);
  window.addEventListener('load', measureHeader);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', closeModal);
})();
